import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import {
  DeleteTeeSheetNotification,
  GetTeeSheetNotification,
  PutTeeSheetNotification,
  TTeesheetNotificationType,
} from "api/rpc/2024-04/facilityAdmin/teesheet/notification";

import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Popup from "components/popup/Popup";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import TextField from "components/form/textField/TextField";
import { Select } from "components/select";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import useUnsavedChangesContainer from "hooks/useUnsavedChangesContainer/useUnsavedChangesContainer";
import { isEmpty } from "helpers/Helpers";

interface ITeeSheetNotificationSaveState {
  startDate: Date;
  endDate: Date;
  content: string;
  courseId: number;
  id: string;
  subject: string;
  type: string;
  userType: TTeesheetNotificationType;
}

interface INotificationUserTypeOption {
  label: string;
  value: TTeesheetNotificationType;
}

export default function TeeSheetNotification() {
  const { notificationId } = useParams<{ notificationId: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const NO_COURSE_ID = -1;

  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);

  const notificationUserTypeOptions: INotificationUserTypeOption[] = useMemo(() => {
    return [
      {
        label: t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.001"),
        value: "admin",
      },
      {
        label: t("secure.facility.settings.tee_sheets.tee_sheet_notifications_new.002"),
        value: "customer",
      },
    ];
  }, []);

  const [deletePopupVisible, setDeletePopupVisible] = useState<boolean>(false);
  const [loadedNotification, setLoadedNotification] = useState<boolean>(false);

  const [saveStateContainer, saveStateContainerActions] = useUnsavedChangesContainer<ITeeSheetNotificationSaveState>(
    (value, other) => {
      if (value instanceof Date && other instanceof Date) {
        return (
          value.getFullYear() === other.getFullYear() &&
          value.getMonth() === other.getMonth() &&
          value.getDate() === other.getDate()
        );
      } else {
        return undefined;
      }
    },
  );

  // populate component
  useEffect(() => {
    if (!loadedNotification) {
      void loadNotification();
    }
  }, [notificationId]);

  /** Formatted String ==> YYYY-MM-DD */
  function dateFromFormattedString(formattedString: string): Date {
    const tempDateArray = formattedString.split("-");

    try {
      const returnDate = new Date(Number(tempDateArray[0]), Number(tempDateArray[1]) - 1, Number(tempDateArray[2]));
      return returnDate;
    } catch (error: any) {
      return new Date();
    }
  }

  async function loadNotification() {
    const tempRes = await GetTeeSheetNotification({ id: notificationId }, true);

    if (tempRes.status !== StatusCode.OK) {
      return;
    }

    const saveState: ITeeSheetNotificationSaveState = {
      startDate: dateFromFormattedString(tempRes.data.start_date),
      endDate: dateFromFormattedString(tempRes.data.end_date),
      content: tempRes.data.content,
      courseId: tempRes.data.course_id ?? NO_COURSE_ID,
      id: notificationId,
      subject: tempRes.data.subject,
      type: tempRes.data.type,
      userType: tempRes.data.user_type,
    };

    saveStateContainerActions.set(saveState);

    setLoadedNotification(true);
  }

  async function saveNotification() {
    if (isEmpty(saveStateContainer.updatedState?.subject) || isEmpty(saveStateContainer.updatedState?.content)) {
      return;
    }

    const res = await PutTeeSheetNotification(
      {
        id: Number(saveStateContainer.updatedState?.id),
        start_date: saveStateContainer.updatedState?.startDate.toISOString().slice(0, 10),
        end_date: saveStateContainer.updatedState?.endDate.toISOString().slice(0, 10),
        course_id:
          saveStateContainer.updatedState?.courseId === NO_COURSE_ID
            ? undefined
            : saveStateContainer.updatedState?.courseId,
        subject: saveStateContainer.updatedState?.subject,
        content: saveStateContainer.updatedState?.content,
        user_type: saveStateContainer.updatedState?.userType,
        type: saveStateContainer.updatedState?.type,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.data));
      return;
    }

    saveStateContainerActions.save();

    history.push("/admin/settings/tee-sheet/notification");
  }

  function cancelUnsavedChanges() {
    saveStateContainerActions.rollback();
  }

  async function handleNotificationDelete() {
    const deleteRes = await DeleteTeeSheetNotification({ id: notificationId }, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(deleteRes.data));
      return;
    }

    history.push("/admin/settings/tee-sheet/notification");
  }

  const primaryAction = {
    content: t("secure.facility.settings.tee_sheets.tee_sheet_notification.003"),
    action: () => setDeletePopupVisible(true),
  };

  return (
    <Page
      title={`${t("secure.facility.settings.tee_sheets.tee_sheet_notification.004")} #${notificationId}`}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("secure.facility.settings.tee_sheets.tee_sheet_notification.005"),
          url: "/admin/settings/tee-sheet/notification",
        },
      ]}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: saveStateContainer.changesExist,
        onAction: saveNotification,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {loadedNotification && (
        <Card>
          <Card.Section>
            <FormLayout>
              {saveStateContainer.updatedState?.startDate && saveStateContainer.updatedState?.endDate && (
                <FormLayout.Group>
                  <DatePickerInput
                    label={t("Notification Date Range")} // TODO: Translation
                    months={1}
                    position="left"
                    startingDate={saveStateContainer.updatedState.startDate}
                    setStartingDate={(startDate: Date) =>
                      saveStateContainerActions.update(prev => ({ ...prev, startDate }))
                    }
                    endingDate={saveStateContainer.updatedState.endDate}
                    setEndingDate={(endDate: Date) =>
                      saveStateContainerActions.update(prev => ({ ...prev, endDate }))
                    }
                  />
                </FormLayout.Group>
              )}
              {facilityStore.facility?.courses?.length > 1 && (
                <FormLayout.Group>
                  <Select
                    label={t("Is this notification specific to a course?")} // TODO: Translation
                    onChange={(courseId: number) => {
                      saveStateContainerActions.update(prev => ({ ...prev, courseId }));
                    }}
                    defaultValue={saveStateContainer.updatedState?.courseId}
                  >
                    <Select.Option value={NO_COURSE_ID} name={"None"}>
                      &nbsp;
                    </Select.Option>
                    {facilityStore.facility?.courses?.map(course => {
                      return (
                        <Select.Option key={course.id} value={course.id} name={course.full_name}>
                          {course.full_name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
              )}
              <FormLayout.Group>
                <Select
                  label={t("secure.facility.settings.tee_sheets.tee_sheet_notification.006")}
                  onChange={(userType: TTeesheetNotificationType) =>
                    saveStateContainerActions.update(prev => ({ ...prev, userType }))
                  }
                  defaultValue={saveStateContainer.updatedState?.userType}
                >
                  {notificationUserTypeOptions.map((option, index: number) => {
                    return (
                      <Select.Option key={index} value={option.value} name={option.label}>
                        {option.label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  value={saveStateContainer.updatedState?.subject}
                  label={t("secure.facility.settings.tee_sheets.tee_sheet_notification.007")}
                  onChange={e => saveStateContainerActions.update(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder={t("secure.facility.settings.tee_sheets.tee_sheet_notification.008")}
                  error={isEmpty(saveStateContainer.updatedState?.subject)}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  value={saveStateContainer.updatedState?.content}
                  label={t("secure.facility.settings.tee_sheets.tee_sheet_notification.009")}
                  onChange={e => saveStateContainerActions.update(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={t("Message Contents")} // TODO: Translation
                  error={isEmpty(saveStateContainer.updatedState?.content)}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      )}

      <Popup
        onCancel={() => setDeletePopupVisible(false)}
        onOk={handleNotificationDelete}
        cancelText={t("secure.facility.settings.tee_sheets.tee_sheet_notification.011")}
        okText={t("secure.facility.settings.tee_sheets.tee_sheet_notification.012")}
        open={deletePopupVisible}
        type="warning"
        title={t("secure.facility.settings.tee_sheets.tee_sheet_notification.013")}
        description={t("secure.facility.settings.tee_sheets.tee_sheet_notification.014")}
      />
    </Page>
  );
}
